import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  Paper,
  Menu,
  MenuItem,
  InputAdornment,
  Chip,
  TextField,
  Grid,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import TextFieldInput from "@mui/material/TextField";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../services/api";
import TableView from "../components/table-view/TableView";
import WestIcon from "@mui/icons-material/West";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";

const ProgressReportPdfs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sys_status = "eq_case" } = location.state || {};

  const [progressReportFiles, setProgressReportFiles] = useState([]);
  const [progressReportLoading, setProgressReportLoading] = useState(false);
  const [paginationCount, setPaginationCount] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [pdfFilter, setPdfFilter] = useState("all"); // "all", "with_pdf", "without_pdf"
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("all"); // "all", "io_pending", "sp_pending", "dig_pending", "adgp_pending", "dgp_completed"
  const [approveButtonFilter, setApproveButtonFilter] = useState("all"); // "all", "can_approve", "cannot_approve"
  const [searchValue, setSearchValue] = useState("");
  const [formData, setFormData] = useState({});
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfViewerSrc, setPdfViewerSrc] = useState("");
  const [monthwisePopupOpen, setMonthwisePopupOpen] = useState(false);
  const [monthwiseData, setMonthwiseData] = useState([]);
  const [monthwiseLoading, setMonthwiseLoading] = useState(false);
  const [monthwiseTotalRecord, setMonthwiseTotalRecord] = useState(0);
  const [monthwiseTotalPage, setMonthwiseTotalPage] = useState(1);
  const [monthwiseCurrentPage, setMonthwiseCurrentPage] = useState(1);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [latestMonthwisePdfs, setLatestMonthwisePdfs] = useState({}); // Store latest PDF per case
  const [approvalStatuses, setApprovalStatuses] = useState({}); // Store approval status per case
  const [progressReportRecords, setProgressReportRecords] = useState({}); // Store progress report records per case
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");
  const [approvalDate, setApprovalDate] = useState(null);
  const [selectedCaseForApproval, setSelectedCaseForApproval] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  // IO Selected popup state (shows selected progress report records + approval form)
  const [ioSelectedPopupOpen, setIoSelectedPopupOpen] = useState(false);
  const [selectedCaseForIoPopup, setSelectedCaseForIoPopup] = useState(null);
  const pageSize = 10;
  const isFetchingPdfsRef = useRef(false); // Track if we're currently fetching PDFs
  const fetchedCaseIdsRef = useRef(new Set()); // Track which case IDs we've already fetched
  const fetchedApprovalStatusesRef = useRef(new Set()); // Track which case IDs we've already fetched approval status for

  const REACT_APP_SERVER_URL_FILE_VIEW = process.env.REACT_APP_SERVER_URL_FILE_VIEW;

  const buildFileUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
      return filePath;
    }

    let base = (REACT_APP_SERVER_URL_FILE_VIEW || "").replace(/\/$/, "");
    let normalizedPath = filePath.replace(/\\/g, '/');
    
    // Remove leading/trailing slashes from path
    normalizedPath = normalizedPath.replace(/^\/+|\/+$/g, '');
    
    // If path starts with 'files/', remove it
    if (normalizedPath.startsWith('files/')) {
      normalizedPath = normalizedPath.replace(/^files\//, '');
    }
    
    // Ensure base ends with '/files' for the static file route
    if (!base.endsWith('/files')) {
      base = base.endsWith('/') ? `${base}files` : `${base}/files`;
    }
    
    // Construct final URL: base/files/filename
    return `${base}/${normalizedPath}`;
  };

  // Build PDF viewer URL - for localhost, use direct URL; for production, could use Google viewer
  const buildPdfViewerUrl = (filePath) => {
    if (!filePath) return null;
    const directUrl = buildFileUrl(filePath);
    if (!directUrl) return null;
    
    // For localhost, use direct URL (Google viewer doesn't work with localhost)
    // For production/public URLs, could use Google viewer if needed
    if (directUrl.includes('localhost') || directUrl.includes('127.0.0.1')) {
      return directUrl;
    }
    
    // For production, could use Google viewer, but for now use direct URL
    return directUrl;
    // Uncomment below for production with public URLs:
    // return `https://docs.google.com/viewer?url=${encodeURIComponent(directUrl)}&embedded=true`;
  };

  // Store all fetched records for filtering
  const [allFetchedFiles, setAllFetchedFiles] = useState([]);
  const [isLoadingAllFiles, setIsLoadingAllFiles] = useState(false);

  // Fetch all records when filters are applied (for filtering)
  const fetchAllProgressReportFiles = async () => {
    setIsLoadingAllFiles(true);
    try {
      const allowedUserIds = JSON.parse(localStorage.getItem("allowedUserIds") || "[]");
      const allowedDivisionIds = JSON.parse(localStorage.getItem("allowedDivisionIds") || "[]");
      const allowedDepartmentIds = JSON.parse(localStorage.getItem("allowedDepartmentIds") || "[]");
      const getDataBasesOnUsers = JSON.parse(localStorage.getItem("getDataBasesOnUsers") || "false");
      const role_title = localStorage.getItem("role_title") || "";

      const payload = {
        allowedUserIds,
        allowedDivisionIds,
        allowedDepartmentIds,
        getDataBasesOnUsers,
        role_title,
        sys_status,
        page: 1,
        limit: 10000, // Fetch all records for filtering
      };

      const response = await api.post("/templateData/getUserProgressReportFinalPdfs", payload);

      if (response?.success) {
        setAllFetchedFiles(response?.data || []);
      } else {
        setAllFetchedFiles([]);
      }
    } catch (error) {
      setAllFetchedFiles([]);
    } finally {
      setIsLoadingAllFiles(false);
    }
  };

  const fetchProgressReportFiles = async (page = 1) => {
    setProgressReportLoading(true);
    try {
      const allowedUserIds = JSON.parse(localStorage.getItem("allowedUserIds") || "[]");
      const allowedDivisionIds = JSON.parse(localStorage.getItem("allowedDivisionIds") || "[]");
      const allowedDepartmentIds = JSON.parse(localStorage.getItem("allowedDepartmentIds") || "[]");
      const getDataBasesOnUsers = JSON.parse(localStorage.getItem("getDataBasesOnUsers") || "false");
      const role_title = localStorage.getItem("role_title") || "";

      const payload = {
        allowedUserIds,
        allowedDivisionIds,
        allowedDepartmentIds,
        getDataBasesOnUsers,
        role_title,
        sys_status,
        page: page,
        limit: pageSize, // Always use pagination
      };

      const response = await api.post("/templateData/getUserProgressReportFinalPdfs", payload);

      if (response?.success) {
        setProgressReportFiles(response?.data || []);
        setTotalPage(response?.meta?.totalPages || 1);
        setTotalRecord(response?.meta?.totalItems || 0);
      } else {
        setProgressReportFiles([]);
        const errorMessage = response?.message || "Failed to fetch Progress Report files.";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "toast-error",
        });
      }
    } catch (error) {
      setProgressReportFiles([]);
      toast.error(error?.response?.data?.message || "Please try again!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: "toast-error",
      });
    } finally {
      setProgressReportLoading(false);
    }
  };

  // Check if any filter is applied
  const hasFilters = pdfFilter !== "all" || 
                     approvalStatusFilter !== "all" || 
                     approveButtonFilter !== "all" || 
                     (searchValue && searchValue.trim() !== "");

  // Fetch all records when filters are applied
  useEffect(() => {
    if (hasFilters) {
      fetchAllProgressReportFiles();
    } else {
      setAllFetchedFiles([]);
    }
  }, [pdfFilter, approvalStatusFilter, approveButtonFilter, searchValue]);

  // Fetch paginated records when no filters, or when pagination changes and no filters
  useEffect(() => {
    if (!hasFilters) {
      fetchProgressReportFiles(paginationCount);
    }
  }, [paginationCount, hasFilters]);

  const handlePagination = (page) => {
    setPaginationCount(page);
  };

  const handlePdfFilterChange = (event) => {
    setPdfFilter(event.target.value);
    setPaginationCount(1);
  };

  const handleApprovalStatusFilterChange = (event) => {
    setApprovalStatusFilter(event.target.value);
    setPaginationCount(1);
  };

  const handleApproveButtonFilterChange = (event) => {
    setApproveButtonFilter(event.target.value);
    setPaginationCount(1);
  };


  const getFilterLabel = () => {
    switch (pdfFilter) {
      case "with_pdf":
        return "With PDF";
      case "without_pdf":
        return "Without PDF";
      default:
        return "All PDFs";
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      search: value,
    });
    setSearchValue(value);
    setPaginationCount(1);
  };

  const handleClear = () => {
    setFormData({
      ...formData,
      search: "",
    });
    setSearchValue("");
    setPdfFilter("all");
    setApprovalStatusFilter("all");
    setApproveButtonFilter("all");
    setPaginationCount(1);
  };


  const openPdfInViewer = (filePath) => {
    const fileUrl = filePath ? buildFileUrl(filePath) : null;
    if (fileUrl) {
      setPdfViewerSrc(fileUrl);
      setPdfViewerOpen(true);
    } else {
      toast.info("No PDF available to display.");
    }
  };

  const closePdfViewer = () => {
    setPdfViewerOpen(false);
    setPdfViewerSrc("");
  };

  // Calculate approval status based on approval_field_array
  const calculateApprovalStatus = (approvalFieldArray) => {
    if (!approvalFieldArray || approvalFieldArray.length === 0) {
      return "IO Pending";
    }

    // Parse if it's a string
    let approvalArray = [];
    try {
      approvalArray = typeof approvalFieldArray === "string"
        ? JSON.parse(approvalFieldArray)
        : approvalFieldArray;
    } catch (e) {
      return "IO Pending";
    }

    if (!Array.isArray(approvalArray) || approvalArray.length === 0) {
      return "IO Pending";
    }

    // Check if DGP is in the array (case-insensitive) - if yes, status is DGP Completed
    const hasDGP = approvalArray.some(role => role && role.toUpperCase() === "DGP");
    if (hasDGP) {
      return "DGP Completed";
    }

    // Default approval steps order
    const defaultApprovalSteps = ["IO", "SP", "DIG", "ADGP", "DGP"];
    
    // Find the last approved role (first element is the latest approval)
    const lastApprovedRole = approvalArray[0];
    
    // Case-insensitive matching
    const lastApprovedIndex = defaultApprovalSteps.findIndex(
      step => step.toUpperCase() === (lastApprovedRole || "").toUpperCase()
    );
    
    if (lastApprovedIndex === -1) {
      return "IO Pending";
    }

    // Determine next pending role
    if (lastApprovedIndex < defaultApprovalSteps.length - 1) {
      const nextPendingRole = defaultApprovalSteps[lastApprovedIndex + 1];
      return `${nextPendingRole} Pending`;
    }

    // If all steps are approved (shouldn't happen if DGP check above didn't catch it)
    return "DGP Completed";
  };

  // Fetch progress report records for a case ID to get approval status
  const fetchProgressReportApprovalStatus = async (caseId, isEqCase = false) => {
    if (!caseId) {
      return { status: "IO Pending", records: [] };
    }
    
    try {
      const payload = {
        table_name: "cid_ui_case_progress_report",
        ui_case_id: caseId,
        page: 1,
        limit: 1000, // Fetch all records to check approval status
      };

      const response = await api.post("/templateData/getTemplateData", payload);
      
      if (response && response.success && response.data && response.data.length > 0) {
        // Store records for approval functionality
        setProgressReportRecords(prev => ({
          ...prev,
          [String(caseId)]: response.data
        }));
        
        // Get IO selected record IDs from the first record (all records should have the same value)
        let ioSelectedRecordIds = [];
        if (response.data.length > 0) {
          const firstRecord = response.data[0];
          if (firstRecord.io_selected_record_ids) {
            try {
              ioSelectedRecordIds = typeof firstRecord.io_selected_record_ids === "string"
                ? JSON.parse(firstRecord.io_selected_record_ids)
                : firstRecord.io_selected_record_ids;
            } catch (e) {
              // Will check all records if parsing fails
            }
          }
        }
        
        // If IO has selected records, only check those records for approval status
        // Otherwise, check all records
        const recordsToCheck = ioSelectedRecordIds.length > 0
          ? response.data.filter(rec => ioSelectedRecordIds.map(id => Number(id)).includes(Number(rec.id)))
          : response.data;
        
        // If no records to check, return IO Pending
        if (recordsToCheck.length === 0) {
          return { status: "IO Pending", records: response.data };
        }
        
        // Check selected records - determine the overall approval status
        // Logic: If ANY record has been submitted by IO, show the next pending status
        // Only show "IO Pending" if NO records have been submitted by IO
        const defaultApprovalSteps = ["IO", "SP", "DIG", "ADGP", "DGP"];
        let foundPendingStatus = null;
        let allHaveDGP = true;
        let hasAnyIOApproval = false; // Track if ANY record has IO approval
        let recordsWithoutApproval = 0; // Count records without approval
        let recordsWithApproval = 0; // Count records that have approval
        
        for (const record of recordsToCheck) {
          if (!record.approval_field_array) {
            // No approval array - this record hasn't been submitted
            allHaveDGP = false;
            recordsWithoutApproval++;
            continue; // Continue checking other records
          }
          
          let approvalArray = [];
          try {
            approvalArray = typeof record.approval_field_array === "string"
              ? JSON.parse(record.approval_field_array)
              : record.approval_field_array;
          } catch (e) {
            allHaveDGP = false;
            recordsWithoutApproval++;
            continue; // Continue checking other records
          }
          
          if (!Array.isArray(approvalArray) || approvalArray.length === 0) {
            // Empty array - this record hasn't been submitted
            allHaveDGP = false;
            recordsWithoutApproval++;
            continue; // Continue checking other records
          }
          
          recordsWithApproval++;
          
          // Check if this record has IO approval
          const hasIO = approvalArray.some(role => role && role.toUpperCase() === "IO");
          if (hasIO) {
            hasAnyIOApproval = true;
          }
          
          // Check if this record has DGP approval (case-insensitive)
          const hasDGP = approvalArray.some(role => role && role.toUpperCase() === "DGP");
          if (!hasDGP) {
            allHaveDGP = false;
            
            // Calculate the pending status for this record
            const lastApprovedRole = approvalArray[0]; // First element is latest approval
            
            // Case-insensitive matching
            const lastApprovedIndex = defaultApprovalSteps.findIndex(
              step => step.toUpperCase() === (lastApprovedRole || "").toUpperCase()
            );
            
            if (lastApprovedIndex !== -1 && lastApprovedIndex < defaultApprovalSteps.length - 1) {
              const nextPendingRole = defaultApprovalSteps[lastApprovedIndex + 1];
              const pendingStatus = `${nextPendingRole} Pending`;
              
              // Keep the earliest pending status (IO < SP < DIG < ADGP < DGP)
              if (!foundPendingStatus) {
                foundPendingStatus = pendingStatus;
              } else {
                // Compare which status is earlier in the chain
                const currentPendingIndex = defaultApprovalSteps.findIndex(
                  step => step.toUpperCase() === pendingStatus.replace(" Pending", "").toUpperCase()
                );
                const foundPendingIndex = defaultApprovalSteps.findIndex(
                  step => step.toUpperCase() === foundPendingStatus.replace(" Pending", "").toUpperCase()
                );
                if (currentPendingIndex < foundPendingIndex) {
                  foundPendingStatus = pendingStatus;
                }
              }
            }
          }
        }
        
        // If all selected records have DGP approval, return DGP Completed
        if (allHaveDGP && recordsToCheck.length > 0) {
          return { status: "DGP Completed", records: response.data };
        }
        
        // If ANY record has IO approval, show the next pending status (SP Pending, etc.)
        // Only show "IO Pending" if NO records have been submitted by IO
        if (hasAnyIOApproval && foundPendingStatus) {
          return { status: foundPendingStatus, records: response.data };
        }
        
        // If we have records with approval but no pending status found, check if they all have IO
        if (recordsWithApproval > 0 && hasAnyIOApproval) {
          // At least one record has IO approval, so next step should be SP Pending
          return { status: "SP Pending", records: response.data };
        }
        
        // If no records have been submitted by IO, show IO Pending
        if (!hasAnyIOApproval) {
          return { status: "IO Pending", records: response.data };
        }
        
        // Fallback
        return { status: "IO Pending", records: response.data };
      }
      
      return { status: "IO Pending", records: [] };
    } catch (error) {
      console.error("Error fetching approval status:", error);
      return { status: "IO Pending", records: [] };
    }
  };

  // Fetch approval statuses for multiple cases in batch
  const fetchApprovalStatusesBatch = async (caseIds) => {
    if (!caseIds || caseIds.length === 0) return {};
    
    const statusesMap = {};
    
    // Fetch statuses for all case IDs in parallel
    const statusPromises = caseIds.map(async (caseId) => {
      const result = await fetchProgressReportApprovalStatus(caseId);
      return { caseId: String(caseId), status: result.status };
    });
    
    const results = await Promise.all(statusPromises);
    
    results.forEach(({ caseId, status }) => {
      statusesMap[caseId] = status;
    });
    
    return statusesMap;
  };

  // Check if current user can approve a specific case
  const canApproveCase = (caseId, approvalStatus) => {
    // Removed debug logs
    
    // Check if DGP has already approved - if yes, no one can approve
    if (approvalStatus === "DGP Completed") {
      return false;
    }
    
    // Only roles participating in the approval workflow can approve (SP, DIG, ADGP, DGP)
    if (!approvalStatus || !approvalStatus.includes("Pending")) {
      return false;
    }

    // Extract pending role from status (e.g., "DIG Pending" -> "DIG")
    const pendingRole = approvalStatus.replace(" Pending", "").toUpperCase();
    if (!["SP", "DIG", "ADGP", "DGP"].includes(pendingRole)) {
      return false;
    }
    
    // Get current user's role to check if DGP can approve even when DIG/ADGP is pending
    let roleTitle = "";
    try {
      const roleTitleRaw = localStorage.getItem("role_title") || "";
      if (roleTitleRaw.startsWith('"') && roleTitleRaw.endsWith('"')) {
        roleTitle = JSON.parse(roleTitleRaw);
      } else if (roleTitleRaw.startsWith('"')) {
        roleTitle = JSON.parse(roleTitleRaw + '"');
      } else {
        roleTitle = roleTitleRaw;
      }
    } catch (e) {
      roleTitle = localStorage.getItem("role_title") || "";
    }
    
    const designationName = (localStorage.getItem("designation_name") || "").toLowerCase();
    const roleTitleLower = (roleTitle || "").toLowerCase().trim().replace(/['"]+/g, '');
    const designationNameLower = (designationName || "").toLowerCase();
    
    // Check if user is DGP
    let isDGP = false;
    if (roleTitleLower === "dgp" || roleTitleLower.includes("dgp") || roleTitleLower === "director general of police") {
      isDGP = true;
    } else if (designationNameLower.includes("director general of police") || designationNameLower.includes("dgp")) {
      isDGP = true;
    } else {
      const splitingValue = designationName.split(" ");
      const firstWord = splitingValue?.[0]?.toUpperCase() || "";
      if (firstWord === "DGP") {
        isDGP = true;
      }
    }
    
    // DGP can approve even if DIG or ADGP is pending (DGP can skip)
    if (isDGP && (pendingRole === "DIG" || pendingRole === "ADGP" || pendingRole === "DGP")) {
      return true;
    }

    // Determine user's role - check both role_title and designation_name
    let userRole = "";
    
    
    // Check role_title first
    let roleFromTitle = "";
    // Only match IO if it's exactly "investigation officer" - don't match "io" substring to avoid false matches like "admin organization"
    if (roleTitleLower === "investigation officer" || roleTitleLower === "io") {
      roleFromTitle = "IO";
    } else if (roleTitleLower === "dgp" || roleTitleLower.includes("dgp") || roleTitleLower === "director general of police") {
      roleFromTitle = "DGP";
    } else if (roleTitleLower === "adgp" || roleTitleLower.includes("adgp") || roleTitleLower === "additional director general") {
      roleFromTitle = "ADGP";
    } else if (roleTitleLower === "dig" || roleTitleLower.includes("dig") || roleTitleLower === "deputy inspector general") {
      roleFromTitle = "DIG";
    } else if (roleTitleLower === "sp" || roleTitleLower.includes("sp") || roleTitleLower === "superintendent") {
      roleFromTitle = "SP";
    }
    
    
    // Check designation_name
    let roleFromDesignation = "";
    
    // First, check if first word is exactly DIG, ADGP, DGP, or SP
    const splitingValue = designationName.split(" ");
    const firstWord = splitingValue?.[0]?.toUpperCase() || "";
    
    if (firstWord === "DIG" || firstWord === "ADGP" || firstWord === "DGP" || firstWord === "SP") {
      roleFromDesignation = firstWord;
    } else if (designationNameLower.includes("director general of police") || designationNameLower.includes("dgp")) {
      roleFromDesignation = "DGP";
    } else if (designationNameLower.includes("additional director general") || designationNameLower.includes("adgp")) {
      roleFromDesignation = "ADGP";
    } else if (designationNameLower.includes("deputy inspector general") || designationNameLower.includes("dig")) {
      roleFromDesignation = "DIG";
    } else if (designationNameLower.includes("superintendent") || designationNameLower.includes("sp")) {
      roleFromDesignation = "SP";
    } else {
      // Last fallback: use first word as-is
      if (firstWord) {
        roleFromDesignation = firstWord;
      }
    }
    
    
    // Use role from designation if it's a valid approval role (DIG, ADGP, DGP)
    // Otherwise use role from title, but only if it's also a valid role
    // Priority: designation > title (for approval roles)
    const validApprovalRoles = ["DIG", "ADGP", "DGP", "SP", "IO"];
    
    if (roleFromDesignation && validApprovalRoles.includes(roleFromDesignation)) {
      userRole = roleFromDesignation;
    } else if (roleFromTitle && validApprovalRoles.includes(roleFromTitle)) {
      userRole = roleFromTitle;
    } else {
      userRole = roleFromTitle || roleFromDesignation;
    }
    
    // Special case: DGP can approve even if DIG or ADGP is pending (DGP can skip)
    if (userRole === "DGP" && (pendingRole === "DIG" || pendingRole === "ADGP" || pendingRole === "DGP")) {
      return true;
    }
    
    // Check if user's role matches the pending role
    const canApprove = userRole === pendingRole;
    
    return canApprove;
  };

  // Handle approval submission
  const handleSubmitApproval = async (caseId, comment = "", date = null) => {
    const defaultApprovalSteps = ["IO", "SP", "DIG", "ADGP", "DGP"];
    
    // Get current user's role - use same logic as canApproveCase
    let roleTitle = "";
    try {
      const roleTitleRaw = localStorage.getItem("role_title") || "";
      if (roleTitleRaw.startsWith('"') && roleTitleRaw.endsWith('"')) {
        roleTitle = JSON.parse(roleTitleRaw);
      } else if (roleTitleRaw.startsWith('"')) {
        roleTitle = JSON.parse(roleTitleRaw + '"');
      } else {
        roleTitle = roleTitleRaw;
      }
    } catch (e) {
      roleTitle = localStorage.getItem("role_title") || "";
    }
    
    const designationName = (localStorage.getItem("designation_name") || "").toLowerCase();
    let stepperValue = "";

    const roleTitleLower = (roleTitle || "").toLowerCase().trim().replace(/['"]+/g, '');
    // Priority: Check designation_name FIRST (more specific), then role_title
    // Check designation_name first - it's more reliable
    if (designationName) {
      // Check first word of designation_name (e.g., "DIG EO" -> "dig")
      const splitingValue = designationName.split(" ");
      const firstWord = splitingValue?.[0]?.toUpperCase() || "";
      
      if (firstWord === "DIG" || firstWord === "ADGP" || firstWord === "DGP" || firstWord === "SP") {
        stepperValue = firstWord.toLowerCase();
      } else if (designationName.includes("director general of police") || designationName.includes("dgp")) {
        stepperValue = "dgp";
      } else if (designationName.includes("additional director general") || designationName.includes("adgp")) {
        stepperValue = "adgp";
      } else if (designationName.includes("deputy inspector general") || designationName.includes("dig")) {
        stepperValue = "dig";
      } else if (designationName.includes("superintendent") || designationName.includes("sp")) {
        stepperValue = "sp";
      }
    }
    
    // If not found in designation_name, check role_title
    if (!stepperValue) {
      // Only match IO if it's exactly "investigation officer" or exactly "io" (not substring like "organization")
      if (roleTitleLower === "investigation officer" || roleTitleLower === "io") {
        stepperValue = "io";
      } else if (roleTitleLower === "dgp" || roleTitleLower.includes("dgp") || roleTitleLower === "director general of police") {
        stepperValue = "dgp";
      } else if (roleTitleLower === "adgp" || roleTitleLower.includes("adgp") || roleTitleLower === "additional director general") {
        stepperValue = "adgp";
      } else if (roleTitleLower === "dig" || roleTitleLower.includes("dig") || roleTitleLower === "deputy inspector general") {
        stepperValue = "dig";
      } else if (roleTitleLower === "sp" || roleTitleLower.includes("sp") || roleTitleLower === "superintendent") {
        stepperValue = "sp";
      }
    }
    
    // Final fallback: use first word of designation_name
    if (!stepperValue && designationName) {
      const splitingValue = designationName.split(" ");
      if (splitingValue?.[0]) {
        stepperValue = splitingValue[0].toLowerCase();
      }
    }
    const currentStepIndex = defaultApprovalSteps.findIndex(step => step.toLowerCase() === stepperValue);
    if (currentStepIndex === -1) {
      toast.error("Your role is not part of the approval workflow.", {
        position: "top-right",
        autoClose: 3000,
        className: "toast-error",
      });
      return;
    }

    // Get progress report records for this case
    const records = progressReportRecords[String(caseId)] || [];
    if (records.length === 0) {
      toast.error("No records found to approve.", {
        position: "top-right",
        autoClose: 3000,
        className: "toast-error",
      });
      return;
    }

    // Get approval_field_array from first record
    let approvalFieldArray = [];
    const firstRecord = records[0];
    if (firstRecord.approval_field_array) {
      try {
        approvalFieldArray = typeof firstRecord.approval_field_array === "string"
          ? JSON.parse(firstRecord.approval_field_array)
          : firstRecord.approval_field_array;
      } catch (e) {
        approvalFieldArray = [];
      }
    }

    // Get io_selected_record_ids
    let ioSelectedRecordIds = [];
    if (firstRecord.io_selected_record_ids) {
      try {
        ioSelectedRecordIds = typeof firstRecord.io_selected_record_ids === "string"
          ? JSON.parse(firstRecord.io_selected_record_ids)
          : firstRecord.io_selected_record_ids;
      } catch (e) {
        // If no IO selection, use all record IDs
        ioSelectedRecordIds = records.map(r => r.id);
      }
    } else {
      ioSelectedRecordIds = records.map(r => r.id);
    }

    const lastApprovedRole = approvalFieldArray.length > 0 ? approvalFieldArray[0] : null;
    const lastApprovedIndex = lastApprovedRole ? defaultApprovalSteps.indexOf(lastApprovedRole) : -1;

    // IO can always submit (first step)
    if (currentStepIndex === 0) {
      // IO submit logic - check if already submitted
      if (lastApprovedIndex >= 0 && lastApprovedIndex === currentStepIndex) {
        toast.error("IO has already submitted.", {
          position: "top-right",
          autoClose: 3000,
          className: "toast-error",
        });
        return;
      }
    } else {
      // Check if SP has approved (required for DIG/ADGP/DGP)
      const hasSPApproved = approvalFieldArray.includes("SP") || 
        approvalFieldArray.some(role => role.toUpperCase().includes("SP"));
      
      // Special logic for DGP: Can approve if SP has approved, even if DIG/ADGP haven't
      const isDGP = stepperValue === "dgp";
      if (isDGP) {
        if (!hasSPApproved) {
          toast.error("SP must approve before DGP can submit.", {
            position: "top-right",
            autoClose: 3000,
            className: "toast-error",
          });
          return;
        }
        // Check if DGP has already approved
        if (approvalFieldArray.includes("DGP")) {
          toast.error("DGP has already approved.", {
            position: "top-right",
            autoClose: 3000,
            className: "toast-error",
          });
          return;
        }
      } else {
        // For SP, DIG, ADGP: Can only submit if previous step is approved
        if (lastApprovedIndex < currentStepIndex - 1) {
          const previousStep = defaultApprovalSteps[currentStepIndex - 1];
          toast.error(`${previousStep} must approve before you can submit.`, {
            position: "top-right",
            autoClose: 3000,
            className: "toast-error",
          });
          return;
        }
        
        // Check if current step is already approved
        if (lastApprovedIndex >= currentStepIndex) {
          toast.error("This step has already been approved.", {
            position: "top-right",
            autoClose: 3000,
            className: "toast-error",
          });
          return;
        }
      }
    }

    // Check if comment is required (optional for DGP, required for others, NOT required for IO)
    const isDGP = stepperValue === "dgp";
    const isIO = stepperValue === "io";
    
    // For IO, comment is not required, but we still allow date
    // For others, validate comment if not provided
    if (!isIO && !comment && !isDGP) {
      toast.error("Comment is required", {
        position: "top-right",
        autoClose: 3000,
        className: "toast-error",
      });
      return;
    }

    try {
      setApprovalLoading(true);

      const currentStep = defaultApprovalSteps[currentStepIndex];
      const currentApprovalArray = Array.isArray(approvalFieldArray) ? [...approvalFieldArray] : [];
      if (!currentApprovalArray.includes(currentStep)) {
        currentApprovalArray.unshift(currentStep);
      }

      const formData = new FormData();
      const normalData = {
        approval_field_array: JSON.stringify(currentApprovalArray),
      };

      // Add comment based on role
      if (comment) {
        if (stepperValue === "sp") {
          normalData["sp_comment"] = comment;
        } else if (stepperValue === "dig") {
          normalData["dig_comment"] = comment;
        } else if (stepperValue === "adgp") {
          normalData["adgp_comment"] = comment;
        } else if (stepperValue === "dgp") {
          normalData["dgp_comment"] = comment;
        }
      }

      // Add date if provided
      if (date) {
        normalData["submission_date"] = dayjs(date).format("YYYY-MM-DD");
      }

      // Add current logged-in approval person's information
      const designationName = localStorage.getItem("designation_name") || "";
      const userName = localStorage.getItem("username") || "";
      const userId = localStorage.getItem("user_id") || "";
      
      // Set field_approval_done_by with designation name or username
      if (designationName) {
        normalData["field_approval_done_by"] = designationName;
      } else if (userName) {
        normalData["field_approval_done_by"] = userName;
      } else if (userId) {
        normalData["field_approval_done_by"] = `User ID: ${userId}`;
      }

      // Also save who made the comment in the specific comment_by fields so PDFs and displays can show the author
      if (comment) {
        const commenter = normalData["field_approval_done_by"] || designationName || userName || (userId ? `User ID: ${userId}` : "");
        if (stepperValue === "sp") {
          normalData["sp_comment_by"] = commenter;
        } else if (stepperValue === "dig") {
          normalData["dig_comment_by"] = commenter;
        } else if (stepperValue === "adgp") {
          normalData["adgp_comment_by"] = commenter;
        } else if (stepperValue === "dgp") {
          normalData["dgp_comment_by"] = commenter;
        }
      }

      // Add io_selected_record_ids
      normalData["io_selected_record_ids"] = JSON.stringify(ioSelectedRecordIds);

      // Update only IO selected records
      const recordIds = ioSelectedRecordIds.map(id => String(id)).join(",");

      formData.append("table_name", "cid_ui_case_progress_report");
      formData.append("id", recordIds);
      formData.append("data", JSON.stringify(normalData));
      formData.append("transaction_id", "TXN_" + Date.now() + "_" + Math.floor(Math.random() * 1000000));
      formData.append("user_designation_id", localStorage.getItem("designation_id") || null);

      const updateRes = await api.post(
        "/templateData/updateDataWithApprovalToTemplates",
        formData
      );
      setApprovalLoading(false);

      if (updateRes && updateRes.success) {
        setApprovalDialogOpen(false);
        setApprovalComment("");
        setApprovalDate(null);
        setSelectedCaseForApproval(null);

        // Refresh approval statuses and records
        const statusResult = await fetchProgressReportApprovalStatus(caseId);
        setApprovalStatuses(prev => ({
          ...prev,
          [String(caseId)]: statusResult.status
        }));
        setProgressReportRecords(prev => ({
          ...prev,
          [String(caseId)]: statusResult.records || []
        }));

        // If this approval was performed from the IO Selected popup, close the popup.
        // Additionally, if the approver was SP, clear IO selections so the "View Selected" button disappears.
        if (selectedCaseForIoPopup && String(selectedCaseForIoPopup) === String(caseId)) {
          // Close the popup immediately
          handleCloseIoPopup();

          // If SP approved, clear IO selection in local state for the case
          if (stepperValue === "sp") {
            setProgressReportRecords(prev => {
              const copy = { ...prev };
              const key = String(caseId);
              if (copy[key] && Array.isArray(copy[key])) {
                copy[key] = copy[key].map(rec => ({ ...rec, io_selected_record_ids: [] }));
              }
              return copy;
            });
          }
        }

        // Generate PDF after approval (for SP, DIG, ADGP, DGP)
        if (stepperValue !== "io" && ioSelectedRecordIds.length > 0) {
          try {
            // Fetch progress report records to append
            const getTemplatePayload = {
              table_name: "cid_ui_case_progress_report",
              ui_case_id: caseId,
            };

            const getTemplateResponse = await api.post("/templateData/getTemplateData", getTemplatePayload);

            if (getTemplateResponse?.success) {
              const dataToAppend = getTemplateResponse.data.filter(
                (item) => item.field_pr_status === "No" && ioSelectedRecordIds.map(id => Number(id)).includes(Number(item.id))
              );

              if (dataToAppend.length > 0) {
                // Fetch AO field values
                const aoFieldResponse = await api.post("/templateData/viewTemplateData", {
                  table_name: "cid_under_investigation",
                  id: caseId,
                });

                const aoFieldValues = aoFieldResponse?.data || {};
                const aoFieldsToInclude = [
                  "field_cid_crime_no./enquiry_no",
                  "field_crime_number_of_ps",
                  "field_name_of_the_police_station",
                  "field_referring_agency",
                  "field_dept_unit",
                  "field_division",
                ];

                const filteredAOFieldValues = {};
                aoFieldsToInclude.forEach((key) => {
                  if (aoFieldValues[key] !== undefined) {
                    filteredAOFieldValues[key] = aoFieldValues[key];
                  }
                });

                // Generate PDF
                const pdfPayload = {
                  selected_row_id: ioSelectedRecordIds,
                  ui_case_id: caseId,
                  appendText: dataToAppend,
                  aoFields: filteredAOFieldValues,
                  created_by: localStorage.getItem("user_id"),
                  submission_date: date ? dayjs(date).format("YYYY-MM-DD") : null,
                };

                await api.post("/templateData/appendToLastLineOfPDF", pdfPayload);
              }
            }
          } catch (pdfError) {
            console.error("Error generating PDF after approval:", pdfError);
            // Don't fail the approval if PDF generation fails, just log it
          }
        }

        toast.success(updateRes.message || "Approval submitted successfully", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "toast-success",
        });
      } else {
        const errorMessage = updateRes.message || "Failed to submit approval. Please try again.";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          className: "toast-error",
        });
      }
    } catch (error) {
      setApprovalLoading(false);
      toast.error(error?.response?.data?.message || "Please Try Again!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        className: "toast-error",
      });
    }
  };

  const handleOpenApprovalDialog = (caseId) => {
    setSelectedCaseForApproval(caseId);
    
    // Get existing submission date from progress report records
    const records = progressReportRecords[String(caseId)] || [];
    let existingDate = null;
    
    if (records.length > 0) {
      // Check all records for submission_date
      for (const record of records) {
        if (record.submission_date) {
          // Parse the date and set it
          try {
            existingDate = dayjs(record.submission_date);
            break; // Use the first date found
          } catch (e) {
            console.error("Error parsing submission_date:", e);
          }
        }
      }
    }
    
    setApprovalDate(existingDate);
    setApprovalDialogOpen(true);
  };

  const handleCloseApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setApprovalComment("");
    setApprovalDate(null);
    setSelectedCaseForApproval(null);
  };

  // Open IO Selected popup (shows selected records on left and approval form on right)
  const handleOpenIoPopup = async (caseId) => {
    if (!caseId) return;
    setSelectedCaseForIoPopup(caseId);
    setIoSelectedPopupOpen(true);

    // Fetch records & status for this case so popup has latest data
    const result = await fetchProgressReportApprovalStatus(caseId);
    setApprovalStatuses(prev => ({ ...prev, [String(caseId)]: result.status }));

    // Pre-populate date if available
    const records = result.records || [];
    let existingDate = null;
    if (records.length > 0) {
      for (const record of records) {
        if (record.submission_date) {
          try {
            existingDate = dayjs(record.submission_date);
            break;
          } catch (e) {
            console.error("Error parsing submission_date:", e);
          }
        }
      }
    }
    setApprovalDate(existingDate || dayjs());
    setApprovalComment("");
  }; 

  const handleCloseIoPopup = () => {
    setIoSelectedPopupOpen(false);
    setSelectedCaseForIoPopup(null);
    setApprovalComment("");
    setApprovalDate(null);
  };

  // Fetch latest monthwise PDFs for multiple cases in one batch call
  const fetchLatestMonthwisePdfsBatch = async (caseIds) => {
    if (!caseIds || caseIds.length === 0) return {};
    
    try {
      // caseIds should be array of { caseId, isEqCase } objects
      const response = await api.post("/templateData/getLatestMonthwisePdfsBatch", {
        case_ids: caseIds
      });
      
      if (response && response.success && response.data) {
        // response.data is an object with caseId (as string) as key and latest PDF record (or null) as value
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching batch monthwise PDFs:", error);
    }
    return {};
  };

  // Fetch all monthwise PDFs for popup
  const fetchMonthwisePdfs = async (caseId, isEqCase = false, page = 1) => {
    if (!caseId) return;
    
    setMonthwiseLoading(true);
    try {
      const payload = isEqCase 
        ? { eq_case_id: caseId, page, limit: pageSize }
        : { ui_case_id: caseId, page, limit: pageSize };
      
      const response = await api.post("/templateData/getMonthWiseByCaseId", payload);
      
      if (response && response.success) {
        setMonthwiseData(response.data || []);
        setMonthwiseTotalRecord(response.totalRecords || 0);
        setMonthwiseTotalPage(Math.ceil((response.totalRecords || 0) / pageSize));
        setMonthwiseCurrentPage(page);
      }
    } catch (error) {
      console.error("Error fetching monthwise PDFs:", error);
      toast.error("Failed to fetch monthwise PDFs", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setMonthwiseLoading(false);
    }
  };

  const handleOpenMonthwisePopup = (row) => {
    const caseId = row.raw?.eq_case_id || row.raw?.ui_case_id;
    const isEqCase = !!row.raw?.eq_case_id;
    
    if (caseId) {
      setSelectedCaseId({ id: caseId, isEqCase });
      setMonthwisePopupOpen(true);
      fetchMonthwisePdfs(caseId, isEqCase, 1);
    }
  };

  const handleCloseMonthwisePopup = () => {
    setMonthwisePopupOpen(false);
    setMonthwiseData([]);
    setSelectedCaseId(null);
    setMonthwiseCurrentPage(1);
  };

  const handleMonthwisePagination = (page) => {
    if (selectedCaseId) {
      fetchMonthwisePdfs(selectedCaseId.id, selectedCaseId.isEqCase, page);
    }
  };

  // File upload table view helper (similar to ProgressReport.jsx)
  const fileUploadTableView = (attachment, filePath) => {
    if (attachment && attachment !== "") {
      const separateAttachment = attachment.split(",");
      return (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", height: '100%' }}
          onClick={(e) => {
            e.stopPropagation();
            const url = buildFileUrl(filePath);
            if (url) {
              window.open(url, "_blank");
            }
          }}
        >
          <Box className="Roboto attachmentTableBox">
            <span style={{ display: "flex" }}>
              📄
            </span>
            <span className="Roboto attachmentTableName">
              {separateAttachment[0]}
            </span>
          </Box>
          {separateAttachment.length > 1 && (
            <button className="Roboto attachmentTableBtn">
              {separateAttachment.length - 1}+
            </button>
          )}
        </Box>
      );
    }
    return null;
  };

  // Filter files based on PDF filter, approval status filter, approve button filter, and search
  const filteredFiles = useMemo(() => {
    // Use allFetchedFiles when filters are applied, otherwise use progressReportFiles
    const hasAnyFilter = pdfFilter !== "all" || 
                         approvalStatusFilter !== "all" || 
                         approveButtonFilter !== "all" || 
                         (searchValue && searchValue.trim() !== "");
    let files = hasAnyFilter ? (allFetchedFiles || []) : (progressReportFiles || []);
    
    // Apply PDF filter - check for monthwise PDF instead of final PDF
    if (pdfFilter === "with_pdf") {
      files = files.filter((r) => {
        const caseId = r.eq_case_id || r.ui_case_id;
        const latestPdf = latestMonthwisePdfs[caseId];
        // Only include if we've checked and confirmed there's a monthwise PDF
        return latestPdf && latestPdf.monthwise_file_path;
      });
    } else if (pdfFilter === "without_pdf") {
      files = files.filter((r) => {
        const caseId = r.eq_case_id || r.ui_case_id;
        const latestPdf = latestMonthwisePdfs[caseId];
        // Include if we've checked and confirmed there's no monthwise PDF
        // (latestPdf === null means we checked and found nothing)
        // (latestPdf === undefined means we haven't checked yet, exclude from filter)
        return latestPdf === null || (latestPdf && !latestPdf.monthwise_file_path);
      });
    }
    
    // Apply approval status filter
    if (approvalStatusFilter !== "all") {
      files = files.filter((r) => {
        const caseId = r.eq_case_id || r.ui_case_id;
        const caseIdStr = String(caseId);
        const approvalStatus = approvalStatuses[caseIdStr] || "IO Pending";
        
        switch (approvalStatusFilter) {
          case "io_pending":
            return approvalStatus === "IO Pending";
          case "sp_pending":
            return approvalStatus === "SP Pending";
          case "dig_pending":
            return approvalStatus === "DIG Pending";
          case "adgp_pending":
            return approvalStatus === "ADGP Pending";
          case "dgp_pending":
            return approvalStatus === "DGP Pending";
          case "dgp_completed":
            return approvalStatus === "DGP Completed";
          default:
            return true;
        }
      });
    }
    
    // Apply approve button filter
    if (approveButtonFilter !== "all") {
      files = files.filter((r) => {
        const caseId = r.eq_case_id || r.ui_case_id;
        const caseIdStr = String(caseId);
        const approvalStatus = approvalStatuses[caseIdStr] || "IO Pending";
        const canApprove = canApproveCase(caseId, approvalStatus);
        
        if (approveButtonFilter === "can_approve") {
          return canApprove;
        } else if (approveButtonFilter === "cannot_approve") {
          return !canApprove;
        }
        return true;
      });
    }
    
    // Apply search filter
    if (searchValue && searchValue.trim() !== "") {
      const searchLower = searchValue.toLowerCase().trim();
      files = files.filter((r) => {
        const crimeNo = (r.crime_no || "").toLowerCase();
        const cidEnquiry = (r.cid_enquiry_no || r.cid_enquiry_number || r.cid_enquiry || "").toLowerCase();
        const policeStation = (r.field_name_of_the_police_station || r.police_station_name || r.police_station || "").toLowerCase();
        return crimeNo.includes(searchLower) || 
               cidEnquiry.includes(searchLower) || 
               policeStation.includes(searchLower);
      });
    }
    
    return files;
  }, [allFetchedFiles, progressReportFiles, pdfFilter, approvalStatusFilter, approveButtonFilter, searchValue, latestMonthwisePdfs, approvalStatuses]);

  // Calculate filtered totals for pagination
  const filteredTotalRecord = useMemo(() => {
    // If any filter or search is applied, use filtered count; otherwise use backend total
    if (pdfFilter !== "all" || approvalStatusFilter !== "all" || approveButtonFilter !== "all" || (searchValue && searchValue.trim() !== "")) {
      return filteredFiles.length;
    }
    return totalRecord;
  }, [pdfFilter, approvalStatusFilter, approveButtonFilter, totalRecord, filteredFiles.length, searchValue]);

  const filteredTotalPage = useMemo(() => {
    // If any filter or search is applied, calculate pages from filtered count; otherwise use backend total
    if (pdfFilter !== "all" || approvalStatusFilter !== "all" || approveButtonFilter !== "all" || (searchValue && searchValue.trim() !== "")) {
      return Math.ceil(filteredFiles.length / pageSize);
    }
    return totalPage;
  }, [pdfFilter, approvalStatusFilter, approveButtonFilter, totalPage, filteredFiles.length, pageSize, searchValue]);

  // Paginate filtered files for display
  const paginatedFilteredFiles = useMemo(() => {
    // Check if any filter is applied
    const hasAnyFilter = pdfFilter !== "all" || 
                         approvalStatusFilter !== "all" || 
                         approveButtonFilter !== "all" || 
                         (searchValue && searchValue.trim() !== "");
    
    if (!hasAnyFilter) {
      // When no filter/search, use all files (already paginated from backend)
      return filteredFiles;
    }
    // When filter/search is applied, paginate the filtered results
    const startIndex = (paginationCount - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredFiles.slice(startIndex, endIndex);
  }, [filteredFiles, paginationCount, pageSize, pdfFilter, approvalStatusFilter, approveButtonFilter, searchValue]);

  // Fetch latest monthwise PDFs and approval statuses for all files
  // Fetch when files are loaded, regardless of filter (needed to show PDF status in table)
  useEffect(() => {
    // Determine which files to use based on whether filters are applied
    const hasAnyFilter = pdfFilter !== "all" || 
                         approvalStatusFilter !== "all" || 
                         approveButtonFilter !== "all" || 
                         (searchValue && searchValue.trim() !== "");
    const filesToProcess = hasAnyFilter ? allFetchedFiles : progressReportFiles;
    
    // Skip if already fetching or no files
    if (isFetchingPdfsRef.current || filesToProcess.length === 0) {
      return;
    }

    const fetchLatestPdfsAndStatuses = async () => {
      // Prevent multiple concurrent calls
      if (isFetchingPdfsRef.current) {
        return;
      }
      
      isFetchingPdfsRef.current = true;
      const pdfsMap = { ...latestMonthwisePdfs }; // Start with existing PDFs
      const statusesMap = { ...approvalStatuses }; // Start with existing statuses
      const caseIdsToFetch = [];
      const caseIdsForStatus = [];
      
      // Find which case IDs need to be fetched from all files (progressReportFiles or allFetchedFiles)
      // Only fetch if not already in our fetched set or cached map
      for (const r of filesToProcess) {
        const caseId = r.eq_case_id || r.ui_case_id;
        const caseIdStr = String(caseId);
        const isEqCase = !!r.eq_case_id;
        
        // Check for PDFs
        if (caseId && !pdfsMap[caseIdStr] && !fetchedCaseIdsRef.current.has(caseIdStr)) {
          caseIdsToFetch.push({ caseId, isEqCase });
        }
        
        // Check for approval statuses (only for ui_case_id, not eq_case_id)
        if (r.ui_case_id && !statusesMap[caseIdStr] && !fetchedApprovalStatusesRef.current.has(caseIdStr)) {
          caseIdsForStatus.push(r.ui_case_id);
        }
      }
      
      // Fetch PDFs in batch
      if (caseIdsToFetch.length > 0) {
        // Mark all as fetched to prevent duplicate calls (use string keys)
        caseIdsToFetch.forEach(({ caseId }) => {
          fetchedCaseIdsRef.current.add(String(caseId));
        });
        
        // Make ONE batch API call for all case IDs
        const batchResults = await fetchLatestMonthwisePdfsBatch(caseIdsToFetch);
        
        // Merge batch results into pdfsMap (convert caseId to string for consistent key matching)
        Object.keys(batchResults).forEach(caseIdStr => {
          pdfsMap[caseIdStr] = batchResults[caseIdStr];
        });
        
        setLatestMonthwisePdfs(pdfsMap);
      }
      
      // Fetch approval statuses in batch
      if (caseIdsForStatus.length > 0) {
        // Mark all as fetched
        caseIdsForStatus.forEach((caseId) => {
          fetchedApprovalStatusesRef.current.add(String(caseId));
        });
        
        // Fetch approval statuses in batch
        const statusResults = await fetchApprovalStatusesBatch(caseIdsForStatus);
        
        // Merge status results
        Object.keys(statusResults).forEach(caseIdStr => {
          statusesMap[caseIdStr] = statusResults[caseIdStr];
        });
        
        setApprovalStatuses(statusesMap);
      }
      
      isFetchingPdfsRef.current = false;
    };
    
    fetchLatestPdfsAndStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressReportFiles.length, allFetchedFiles.length]);

  const rows = paginatedFilteredFiles.map((r, idx) => {
    const caseId = r.eq_case_id || r.ui_case_id;
    const caseIdStr = String(caseId);
    const latestPdf = latestMonthwisePdfs[caseIdStr] || null;
    const approvalStatus = approvalStatuses[caseIdStr] || "IO Pending";
    // Extract IO selected record ids (if available) from fetched progress report records
    const recordsForCase = progressReportRecords[caseIdStr] || [];
    let ioSelectedRecordIds = [];
    if (recordsForCase.length > 0) {
      // Find a record that has io_selected_record_ids (not all records have it)
      const recordWithIds = recordsForCase.find(rec => rec.io_selected_record_ids);
      if (recordWithIds && recordWithIds.io_selected_record_ids) {
        try {
          const parsed = typeof recordWithIds.io_selected_record_ids === 'string' 
            ? JSON.parse(recordWithIds.io_selected_record_ids) 
            : recordWithIds.io_selected_record_ids;
          // Only use if it's a valid non-empty array
          if (Array.isArray(parsed) && parsed.length > 0) {
            ioSelectedRecordIds = parsed;
          }
        } catch (e) {
          // If parsing fails, keep empty array
          ioSelectedRecordIds = [];
        }
      }
      // If no record has io_selected_record_ids, keep empty array (don't fallback to all records)
    }
    
    return {
      id: caseId || idx,
      sno: (paginationCount - 1) * pageSize + idx + 1,
      crime_no: r.crime_no || "-",
      cid_enquiry: r.cid_enquiry_no || r.cid_enquiry_number || r.cid_enquiry || "-",
      police_station: r.field_name_of_the_police_station || r.police_station_name || r.police_station || "-",
      file_path: r.file_path,
      has_pdf: r.has_pdf,
      created_at: r.created_at,
      latest_monthwise_pdf: latestPdf,
      approval_status: approvalStatus,
      io_selected_record_ids: ioSelectedRecordIds,
      case_id: caseId,
      raw: r,
    };
  });

  // Check if any row has DIG/ADGP/DGP pending to show Approval column
  // Check the approvalStatuses state directly to ensure we catch all cases
  const shouldShowApprovalColumn = useMemo(() => {
    // Check both rows and approvalStatuses state
    const hasPendingInRows = rows.some(row => {
      const status = row.approval_status || "";
      return status.includes("DIG Pending") || status.includes("ADGP Pending") || status.includes("DGP Pending");
    });
    
    const hasPendingInStatuses = Object.values(approvalStatuses).some(status => {
      const statusStr = status || "";
      return statusStr.includes("DIG Pending") || statusStr.includes("ADGP Pending") || statusStr.includes("DGP Pending");
    });
    
    return hasPendingInRows || hasPendingInStatuses;
  }, [rows, approvalStatuses]);

  const columns = useMemo(() => [
    { 
      field: "sno", 
      headerName: "S.No", 
      width: 80,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            S.No
          </span>
        </div>
      ),
    },

    { 
      field: "crime_no", 
      headerName: "Crime Number Of Ps", 
      width: 220,
      minWidth: 180,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            Crime Number Of Ps
          </span>
        </div>
      ),
    },
    {
      field: "police_station",
      headerName: "Police Station",
      width: 250,
      minWidth: 200,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            Police Station
          </span>
        </div>
      ),
    },
    {
      field: "cid_enquiry",
      headerName: "CID Crime No./Enquiry No",
      width: 250,
      minWidth: 200,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            CID Crime No./Enquiry No
          </span>
        </div>
      ),
    },
    {
      field: "approval_status",
      headerName: "Status",
      width: 180,
      minWidth: 150,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            Status
          </span>
        </div>
      ),
      renderCell: (params) => {
        const status = params.row.approval_status || "IO Pending";
        const isCompleted = status === "DGP Completed";

        // Map backend statuses to display text
        let displayStatus = status;
        if (status === "DGP Completed") {
          displayStatus = "DGP Approved";
        } else if (status === "IO Pending") {
          displayStatus = "PR not submitted";
        } else if (status.includes("Pending")) {
          displayStatus = status.replace(" Pending", " approval Pending");
        }

        // Different colors for each approval status
        let chipColor = "#333";
        let chipBackgroundColor = "#e5e7eb";
        
        if (isCompleted) {
          chipColor = "white";
          chipBackgroundColor = "#22c55e"; // Green for completed
        } else if (status === "IO Pending") {
          chipColor = "white";
          chipBackgroundColor = "#ef4444"; // Red for PR not submitted
        } else if (status === "SP Pending") {
          chipColor = "white";
          chipBackgroundColor = "#f59e0b"; // Amber/Orange for SP Pending
        } else if (status === "DIG Pending") {
          chipColor = "white";
          chipBackgroundColor = "#3b82f6"; // Blue for DIG Pending
        } else if (status === "ADGP Pending") {
          chipColor = "white";
          chipBackgroundColor = "#8b5cf6"; // Purple for ADGP Pending
        } else if (status === "DGP Pending") {
          chipColor = "white";
          chipBackgroundColor = "#6366f1"; // Indigo for DGP Pending
        }

        return (
          <Chip
            label={displayStatus}
            size="small"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: "12px",
              color: chipColor,
              backgroundColor: chipBackgroundColor,
              borderRadius: "4px",
              textTransform: "none",
            }}
          />
        );
      },
    },

    {
      field: "io_selected",
      headerName: "PR Approve",
      width: 180,
      minWidth: 140,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            PR Approve
          </span>
        </div>
      ),
      renderCell: (params) => {
        const approvalStatus = params.row.approval_status || "IO Pending";
        const ids = params.row.io_selected_record_ids || [];
        const isStagePending = ["SP Pending", "DIG Pending", "ADGP Pending", "DGP Pending"].some(s => approvalStatus.includes(s));
        let roleTitle = "";
          try {
            const roleRaw = localStorage.getItem("role_title") || "";
            roleTitle = (roleRaw.startsWith('"') && roleRaw.endsWith('"')) ? roleRaw.slice(1, -1) : roleRaw;
          } catch (e) {
            roleTitle = localStorage.getItem("role_title") || "";
          }
          const roleNormalized = (roleTitle || "").toLowerCase().trim().replace(/['"]+/g, '');
          const isRoleNotAllowed = roleNormalized === "investigation officer" || roleNormalized === "io";

        const canApprove = canApproveCase(params.row.case_id, approvalStatus);
        // Show the button when there are IO-selected ids and the approval stage is pending.
        // Previously this required the current user to be able to approve (`canApprove`),
        // which hid the button for other roles. Keep `canApprove` for styling/behavior
        // but allow viewing by anyone with selected IDs.
        const showButton = ids && ids.length > 0 && isStagePending && !isRoleNotAllowed;

        if (!showButton) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%", pt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>-</Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%", pt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenIoPopup(params.row.case_id);
              }}
              disabled={!canApprove}
            >
              View Approval Record ({ids.length})
            </Button>
          </Box>
        );
      },
    },

    {
      field: "latest_monthwise_pdf",
      headerName: "MonthWise PDF",
      width: 150,
      minWidth: 120,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            MonthWise PDF
          </span>
        </div>
      ),
      renderCell: (params) => {
        const latestPdf = params.row.latest_monthwise_pdf;
        const caseId = params.row.case_id;
        if (latestPdf && latestPdf.monthwise_file_path) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%", pt: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openPdfInViewer(latestPdf.monthwise_file_path);
                }}
              >
                Open PDF
              </Button>
            </Box>
          );
        }
        return (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%", pt: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              No PDF
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "view_month",
      headerName: "Action",
      width: 130,
      minWidth: 120,
      renderHeader: () => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span style={{ color: "#1D2939", fontSize: "15px", fontWeight: "500" }}>
            Action
          </span>
        </div>
      ),
      renderCell: (params) => {
        return (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenMonthwisePopup(params.row);
            }}
          >
            View
          </Button>
        );
      },
    },
  ], [shouldShowApprovalColumn]);

  return (
    <Box sx={{ p: 2, bgcolor: "#ffffff", width: "100%", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          pb: 2,
          borderBottom: "1px solid #EEEEEE",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            aria-label="back"
            onClick={() => navigate(-1)}
            sx={{ p: 0 }}
          >
            <WestIcon sx={{ color: "#000", fontSize: 20 }} />
          </IconButton>
          <Typography sx={{ fontSize: "20px", color: "#000000", fontWeight: 600, fontFamily: "Inter" }}>
            Progress Report PDFs
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "end",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextFieldInput
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#475467" }} />
                  </InputAdornment>
                ),
              }}
              onInput={(e) => handleSearchChange(e)}
              value={formData?.search || ''}
              id="tableSearch"
              size="small"
              placeholder="Search"
              variant="outlined"
              className="profileSearchClass"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  // Search is already handled in real-time
                }
              }}
              sx={{
                width: "300px",
                borderRadius: "6px",
                outline: "none",
                "& .MuiInputBase-input::placeholder": {
                  color: "#475467",
                  opacity: "1",
                  fontSize: "14px",
                  fontWeight: "400",
                  fontFamily: "Roboto",
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: "14px", fontFamily: "Roboto" }}>PDF Filter</InputLabel>
              <Select
                value={pdfFilter}
                label="PDF Filter"
                onChange={handlePdfFilterChange}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  borderRadius: "6px",
                }}
              >
                <MenuItem value="all" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>All PDFs</MenuItem>
                <MenuItem value="with_pdf" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>With PDF</MenuItem>
                <MenuItem value="without_pdf" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>Without PDF</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: "14px", fontFamily: "Roboto" }}>Approval Status</InputLabel>
              <Select
                value={approvalStatusFilter}
                label="Approval Status"
                onChange={handleApprovalStatusFilterChange}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  borderRadius: "6px",
                }}
              >
                <MenuItem value="all" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>All Status</MenuItem>
                <MenuItem value="io_pending" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>PR not submitted</MenuItem>
                <MenuItem value="sp_pending" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>SP Pending</MenuItem>
                <MenuItem value="dig_pending" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>DIG Pending</MenuItem>
                <MenuItem value="adgp_pending" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>ADGP Pending</MenuItem>
                <MenuItem value="dgp_pending" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>DGP Pending</MenuItem>
                <MenuItem value="dgp_completed" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>DGP Approved</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontSize: "14px", fontFamily: "Roboto" }}>Approve Button</InputLabel>
              <Select
                value={approveButtonFilter}
                label="Approve Button"
                onChange={handleApproveButtonFilterChange}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Roboto",
                  borderRadius: "6px",
                }}
              >
                <MenuItem value="all" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>All</MenuItem>
                <MenuItem value="can_approve" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>Can Approve</MenuItem>
                <MenuItem value="cannot_approve" sx={{ fontSize: "14px", fontFamily: "Roboto" }}>Cannot Approve</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {(formData?.search || pdfFilter !== "all" || approvalStatusFilter !== "all" || approveButtonFilter !== "all") && (
            <Typography
              onClick={handleClear}
              sx={{
                fontSize: "13px",
                fontWeight: "500",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              mt={1}
            >
              View All / Clear Filter
            </Typography>
          )}
        </Box>
      </Box>

      {progressReportLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (

<Box py={2} sx={{ width: "100%", maxWidth: "100%", overflowX: "auto", overflowY: "visible" }}>
          <TableView
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            handlePagination={handlePagination}
            paginationCount={paginationCount}
            totalPage={filteredTotalPage}
            totalRecord={filteredTotalRecord}
            emptyMessage={
              pdfFilter === "all" 
                ? "No Progress Report PDFs found." 
                : pdfFilter === "with_pdf"
                ? "No Progress Report PDFs with PDF found."
                : "No Progress Report PDFs without PDF found."
            }
          />
        </Box>
      )}

      {/* PDF Viewer Dialog */}
      <Dialog
        open={pdfViewerOpen}
        onClose={closePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: "90%",
            height: "90%",
            maxWidth: "90%",
            maxHeight: "90%",
            margin: 0,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #EEEEEE",
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
              Progress Report PDF
            </Typography>
            <IconButton onClick={closePdfViewer} sx={{ p: 0 }}>
              <ClearIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: "hidden", position: "relative", backgroundColor: "#f5f5f5" }}>
            {pdfViewerSrc ? (
              <Box 
                sx={{ 
                  width: '100%', 
                  height: '100%',
                  border: '0.5px solid #ddd', 
                  borderRadius: '2px',
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5',
                  position: 'relative'
                }}
              >
                {/* Use embed tag for Chrome, iframe for other browsers - same as ProgressReport.jsx */}
                {(() => {
                  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
                  return isChrome ? (
                    // Chrome workaround: Use embed tag instead of iframe
                    <embed
                      key={pdfViewerSrc}
                      src={buildPdfViewerUrl(pdfViewerSrc) || pdfViewerSrc}
                      type="application/pdf"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        backgroundColor: '#f5f5f5'
                      }}
                      title="Progress Report PDF"
                    />
                  ) : (
                    // Other browsers: Use iframe
                    <iframe
                      key={pdfViewerSrc}
                      src={buildPdfViewerUrl(pdfViewerSrc) || pdfViewerSrc}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        backgroundColor: '#f5f5f5'
                      }}
                      title="Progress Report PDF"
                      allow="fullscreen"
                    />
                  );
                })()}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography>No PDF to display</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Monthwise PDFs Popup Dialog */}
      <Dialog
        open={monthwisePopupOpen}
        onClose={handleCloseMonthwisePopup}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: "90%",
            height: "90%",
            maxWidth: "90%",
            maxHeight: "90%",
            margin: 0,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #EEEEEE",
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
              Monthwise PDFs
            </Typography>
            <IconButton onClick={handleCloseMonthwisePopup} sx={{ p: 0 }}>
              <ClearIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {monthwiseLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
                <TableView
                  rows={monthwiseData.map((item, idx) => ({
                    id: item.id || idx,
                    serial_no: (monthwiseCurrentPage - 1) * pageSize + idx + 1,
                    month_of_the_file: item.month_of_the_file || "-",
                    monthwise_file_name: item.monthwise_file_name || "",
                    monthwise_file_path: item.monthwise_file_path || "",
                    submission_date: item.submission_date || null,
                    raw: item,
                  }))}
                  columns={[
                    {
                      headerName: "S.No",
                      field: "serial_no",
                      width: 70,
                      sortable: false,
                      filterable: false,
                      renderCell: (params) => {
                        return "PR" + params.row.serial_no;
                      },
                    },
                    {
                      headerName: "Month of Submission",
                      field: "month_of_the_file",
                      width: 250,
                      renderCell: (params) => {
                        return params.row.month_of_the_file || "-";
                      },
                    },
                    {
                      headerName: "File",
                      field: "monthwise_file_name",
                      width: 300,
                      renderCell: (params) => {
                        return fileUploadTableView(
                          params.row.monthwise_file_name,
                          params.row.monthwise_file_path
                        );
                      },
                    },
                    {
                      headerName: "Submission Date",
                      field: "submission_date",
                      width: 200,
                      minWidth: 180,
                      renderCell: (params) => {
                        const submissionDate = params.row.raw?.submission_date || params.row.submission_date;
                        if (!submissionDate) {
                          return (
                            <Box sx={{ py: 1, px: 1 }}>
                              <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                                -
                              </Typography>
                            </Box>
                          );
                        }
                        
                        // Format the date
                        let formattedDate = "";
                        try {
                          formattedDate = dayjs(submissionDate).format("DD/MM/YYYY");
                        } catch (e) {
                          formattedDate = submissionDate;
                        }
                        
                        return (
                          <Box sx={{ py: 1, px: 1 }}>

                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: "#475467",
                                fontSize: "13px",
                                fontFamily: "Roboto"
                              }}
                            >
                              {formattedDate}
                            </Typography>
                          </Box>
                        );
                      },
                    },
                  ]}
                  getRowId={(r) => r.id}
                  handlePagination={handleMonthwisePagination}
                  paginationCount={monthwiseCurrentPage}
                  totalPage={monthwiseTotalPage}
                  totalRecord={monthwiseTotalRecord}
                  emptyMessage="No monthwise PDFs found."
                />
              </Box>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* IO Selected Popup: left -> selected records; right -> approval form (submit here) */}
      <Dialog
        open={ioSelectedPopupOpen}
        onClose={handleCloseIoPopup}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: "90%", height: "90%", maxWidth: "90%", maxHeight: "90%", margin: 0 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #EEEEEE' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Selected Progress Report Records</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => { if (selectedCaseForIoPopup) handleSubmitApproval(selectedCaseForIoPopup, approvalComment, approvalDate); }}
                disabled={approvalLoading}
                size="small"
              >
                {approvalLoading ? <CircularProgress size={20} color="inherit" /> : 'Submit Approval'}
              </Button>
              <IconButton onClick={handleCloseIoPopup} sx={{ p: 0 }}><ClearIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', gap: 2 }}>
            {/* Left: Selected Records */}
            <Box sx={{ flex: '1 1 60%', overflow: 'auto' }}>
              {selectedCaseForIoPopup ? (() => {
                const caseIdStr = String(selectedCaseForIoPopup);
                const records = progressReportRecords[caseIdStr] || [];
                // Determine IO selected ids - find a record that has io_selected_record_ids
                let ioIds = [];
                if (records.length > 0) {
                  // Find a record that has io_selected_record_ids (not all records have it)
                  const recordWithIds = records.find(rec => rec.io_selected_record_ids);
                  if (recordWithIds && recordWithIds.io_selected_record_ids) {
                    try {
                      const parsed = typeof recordWithIds.io_selected_record_ids === 'string' 
                        ? JSON.parse(recordWithIds.io_selected_record_ids) 
                        : recordWithIds.io_selected_record_ids;
                      // Only use if it's a valid non-empty array
                      if (Array.isArray(parsed) && parsed.length > 0) {
                        ioIds = parsed;
                      }
                    } catch (e) {
                      // If parsing fails, keep empty array
                      ioIds = [];
                    }
                  }
                  // If no record has io_selected_record_ids, keep empty array (don't fallback to all records)
                }

                if (!ioIds || ioIds.length === 0) {
                  return <Typography sx={{ color: 'text.secondary' }}>No IO selected records found.</Typography>;
                }

                const filtered = records.filter(rec => ioIds.map(id => String(id)).includes(String(rec.id)));

                const tableRows = filtered.map((rec, idx) => {
                  let approvalArr = rec.approval_field_array;
                  try {
                    if (typeof approvalArr === 'string') approvalArr = JSON.parse(approvalArr);
                  } catch (e) {
                    // leave as-is
                  }
                  const approvalText = Array.isArray(approvalArr) ? approvalArr.join(', ') : (approvalArr ? String(approvalArr) : '-');
                  return {
                    id: rec.id,
                    sno: idx + 1,
                    record_id: rec.id,
                    action_item: rec.action_item || rec.field_action_item || '-',
                    field_status: rec.field_status || rec.field_pr_status || rec.pr_status || '-',
                    field_due_date: rec.field_due_date || rec.due_date || rec.submission_date || null,
                    approval_array: approvalText,
                    raw: rec,
                  };
                });

                const tableColumns = [
                  {
                    headerName: 'S.No',
                    field: 'sno',
                    width: 70,
                    renderCell: (params) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography sx={{ fontSize: 13 }}>{params.row.sno}</Typography>
                      </Box>
                    ),
                  },
                  {
                    headerName: 'Status',
                    field: 'field_status',
                    width: 120,
                    renderCell: (params) => {
                      const status = params.row.field_status || '-';
                      const isCompleted = status === 'DGP Completed' || status === 'Yes';
                      const isPending = typeof status === 'string' && status.includes('Pending');
                      const color = isCompleted ? '#16a34a' : isPending ? '#b45309' : '#111';
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <Typography sx={{ fontSize: '13px', color }}>{status}</Typography>
                        </Box>
                      );
                    }
                  },
                  {
                    headerName: 'Due Date',
                    field: 'due_date',
                    width: 140,
                    renderCell: (params) => {
                      const rawDate = params.row.field_due_date || params.row.due_date || params.row.raw?.field_due_date || params.row.raw?.due_date || params.row.raw?.duedate || params.row.raw?.submission_date || null;
                      if (!rawDate) return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                          <Typography sx={{ fontSize: 12, color: '#6B7280' }}>-</Typography>
                        </Box>
                      );
                      let formatted = rawDate;
                      try { formatted = dayjs(rawDate).format('DD/MM/YYYY'); } catch (e) { formatted = rawDate; }
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                          <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{formatted}</Typography>
                        </Box>
                      );
                    }
                  },
                  {
                    headerName: 'Action Item',
                    field: 'action_item',
                    minWidth: 300,
                    width: 420,
                    renderCell: (params) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 1 }}>
                        <Typography sx={{ fontSize: 13, color: '#475467', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4, width: '100%' }}>{params.row.action_item}</Typography>
                      </Box>
                    ),
                  },
                ];

                return (
                  <Box>
                    {/* <Typography sx={{ mb: 1 }}>Case ID: <strong>{selectedCaseForIoPopup}</strong></Typography> */}
                    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                      <TableView
                        rows={tableRows}
                        columns={tableColumns}
                        dynamicRowHeight={true}
                        getRowId={(r) => r.id}
                        handlePagination={() => {}}
                        paginationCount={1}
                        totalPage={1}
                        totalRecord={tableRows.length}
                        emptyMessage="No selected records found."
                      />
                    </Box>
                  </Box>
                );
              })() : (
                <Typography sx={{ color: 'text.secondary' }}>No case selected.</Typography>
              )}
            </Box>

            {/* Right: Approval form */}
            <Box sx={{ flex: '0 0 35%', border: '1px solid #EEEEEE', borderRadius: 1, p: 2, bgcolor: '#ffffff' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>Submit Approval</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="Submission Date" value={approvalDate} onChange={(newValue) => setApprovalDate(newValue)} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                </LocalizationProvider>

                {(() => {
                  const roleTitle = JSON.parse(localStorage.getItem('role_title')) || '';
                  const designationName = (localStorage.getItem('designation_name') || '').toLowerCase();
                  const isIO = roleTitle.toLowerCase() === 'investigation officer';
                  const isDGP = designationName.includes('director general of police') || designationName.includes('dgp');
                  if (isIO) return null;
                  return (
                    <TextField multiline rows={10} label={isDGP ? 'Comment (Optional)' : 'Comment'} placeholder={isDGP ? 'Enter your comment here (optional)...' : 'Enter your comment here...'} value={approvalComment} onChange={(e) => setApprovalComment(e.target.value)} fullWidth size="small" required={!isDGP} />
                  );
                })()}

                {/* IO Selected Records hidden as requested */}

                <Box sx={{ height: 0 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={handleCloseApprovalDialog}
        aria-labelledby="approval-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="approval-dialog-title">
          Submit Approval
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Submission Date"
                value={approvalDate}
                onChange={(newValue) => {
                  setApprovalDate(newValue);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  }
                }}
              />
            </LocalizationProvider>
            {(() => {
              const roleTitle = JSON.parse(localStorage.getItem("role_title")) || "";
              const designationName = (localStorage.getItem("designation_name") || "").toLowerCase();
              const isIO = roleTitle.toLowerCase() === "investigation officer";
              const isDGP = designationName.includes("director general of police") || designationName.includes("dgp");
              
              // For IO: Don't show comment field
              if (isIO) {
                return null;
              }
              
              // For DGP: Optional comment
              // For others (SP, DIG, ADGP): Required comment
              return (
                <TextField
                  multiline
                  rows={4}
                  label={isDGP ? "Comment (Optional)" : "Comment "}
                  placeholder={isDGP ? "Enter your comment here (optional)..." : "Enter your comment here..."}
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  fullWidth
                  size="small"
                  required={!isDGP} // Required for SP, DIG, ADGP; optional for DGP
                />
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              handleCloseApprovalDialog();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Validate comment if required
              const roleTitle = JSON.parse(localStorage.getItem("role_title")) || "";
              const designationName = (localStorage.getItem("designation_name") || "").toLowerCase();
              const isDGP = designationName.includes("director general of police") || designationName.includes("dgp");
              const isIO = roleTitle.toLowerCase() === "investigation officer";
              
              if (!isIO && !isDGP && !approvalComment.trim()) {
                toast.error("Comment is required", {
                  position: "top-right",
                  autoClose: 3000,
                });
                return;
              }
              
              if (selectedCaseForApproval) {
                handleSubmitApproval(selectedCaseForApproval, approvalComment, approvalDate);
              }
            }}
            variant="contained"
            color="primary"
            disabled={approvalLoading}
          >
            {approvalLoading ? <CircularProgress size={20} /> : "Submit Approval"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgressReportPdfs;



